/**
 * DuckDB Temporal Value Types & Utilities
 *
 * Constants, type aliases, and utility functions for working with
 * DuckDB temporal values (DATE, TIMESTAMP, TIMESTAMP_MS, TIMESTAMP_S).
 *
 * @module @loader/shared-types/duckdb/temporal
 */

// ============================================================================
// DuckDB Temporal Value Types
// ============================================================================

/**
 * Constants for DuckDB temporal value detection and conversion
 */
export const DUCKDB_TEMPORAL_CONSTANTS = {
  /** Seconds in a day */
  SECONDS_PER_DAY: 86400,
  /** Threshold to detect milliseconds vs seconds (values >= 1e11 are likely ms) */
  EPOCH_MILLISECONDS_THRESHOLD: 1e11,
  /** Threshold to detect seconds vs days (values >= 1e8 are likely seconds) */
  EPOCH_SECONDS_THRESHOLD: 1e8,
} as const;

/**
 * DuckDB date format patterns for parsing string dates
 *
 * Used by both the formula SQL translator (apps/api) and the
 * file processing type coercion system (apps/duckdb-api) to
 * parse date strings in various formats via TRY_STRPTIME.
 *
 * Precedence: patterns are tried in order via COALESCE(TRY_STRPTIME(...)).
 * EU format (DD/MM/YYYY) is used for slash-separated dates.
 * ISO format (YYYY-MM-DD) is handled separately via TRY_CAST before these
 * patterns are attempted.
 */
export const DUCKDB_DATE_FORMAT_PATTERNS = [
  '%d/%m/%Y',                // EU format (DD/MM/YYYY)
  '%d/%m/%y',                // EU format with 2-digit year (DD/MM/YY)
  '%d/%m/%Y %H:%M:%S',      // EU format with time (DD/MM/YYYY HH:MM:SS)
  '%d.%m.%Y',                // EU format with dots (DD.MM.YYYY)
  '%d.%m.%Y %H:%M',         // EU format with dots + time, no seconds (DD.MM.YYYY HH:MM)
  '%d.%m.%Y %H:%M:%S',      // EU format with dots + time (DD.MM.YYYY HH:MM:SS)
  '%d.%m.%Y %H:%M:%S UTC',  // EU format with dots + time + UTC
  '%b %d, %Y',               // Natural format (Jan 15, 2024)
  '%B %d, %Y',               // Natural format (January 15, 2024)
  '%d %b %Y',                // Natural format (15 Jan 2024)
] as const;

/** Date-only patterns (no time component) — derived from DUCKDB_DATE_FORMAT_PATTERNS */
export const DUCKDB_DATE_PATTERNS = DUCKDB_DATE_FORMAT_PATTERNS.filter(p => !p.includes('%H'));

/** Datetime patterns (with time component) — derived from DUCKDB_DATE_FORMAT_PATTERNS */
export const DUCKDB_DATETIME_PATTERNS = DUCKDB_DATE_FORMAT_PATTERNS.filter(p => p.includes('%H'));

/**
 * DuckDB DATE value - INT32 days since Unix epoch (1970-01-01)
 */
export type DuckDBDateValue = number;

/**
 * DuckDB TIMESTAMP value - INT64 microseconds since Unix epoch
 */
export type DuckDBTimestampValue = number;

/**
 * DuckDB TIMESTAMP_MS value - INT64 milliseconds since Unix epoch
 */
export type DuckDBTimestampMillisecondsValue = number;

/**
 * DuckDB TIMESTAMP_S value - INT64 seconds since Unix epoch
 */
export type DuckDBTimestampSecondsValue = number;

/**
 * Union of all DuckDB temporal numeric representations
 */
export type DuckDBTemporalValue =
  | DuckDBDateValue
  | DuckDBTimestampValue
  | DuckDBTimestampMillisecondsValue
  | DuckDBTimestampSecondsValue;

/**
 * Check if a numeric value is likely a DuckDB DATE (days since epoch)
 * Values < 1e8 are likely days (covers dates up to year ~275,000)
 */
export function isDuckDBDateValue(value: number): boolean {
  return Math.abs(value) < DUCKDB_TEMPORAL_CONSTANTS.EPOCH_SECONDS_THRESHOLD;
}

/**
 * Check if a numeric value is likely a DuckDB temporal value (not a regular number)
 * Uses magnitude-based heuristics to detect epoch-based values
 */
export function isDuckDBTemporalValue(value: number): boolean {
  const absValue = Math.abs(value);
  // Very small values could be days, medium could be seconds, large could be ms
  return absValue < DUCKDB_TEMPORAL_CONSTANTS.EPOCH_MILLISECONDS_THRESHOLD * 100;
}

/**
 * Convert a DuckDB temporal value to milliseconds since epoch
 * Uses magnitude-based detection to determine the input format
 *
 * @param value - Numeric value (days, seconds, or milliseconds since epoch)
 * @returns Milliseconds since Unix epoch
 */
export function duckDBTemporalToMs(value: number): number {
  const absValue = Math.abs(value);

  if (absValue < DUCKDB_TEMPORAL_CONSTANTS.EPOCH_SECONDS_THRESHOLD) {
    // Likely days since epoch (DATE)
    return value * DUCKDB_TEMPORAL_CONSTANTS.SECONDS_PER_DAY * 1000;
  } else if (absValue < DUCKDB_TEMPORAL_CONSTANTS.EPOCH_MILLISECONDS_THRESHOLD) {
    // Likely seconds since epoch (TIMESTAMP_S)
    return value * 1000;
  } else {
    // Likely milliseconds since epoch (TIMESTAMP_MS or already converted)
    return value;
  }
}

/**
 * Convert milliseconds since epoch to DuckDB DATE format (days)
 */
export function msToDuckDBDate(ms: number): DuckDBDateValue {
  return Math.floor(ms / (DUCKDB_TEMPORAL_CONSTANTS.SECONDS_PER_DAY * 1000));
}

/**
 * Convert DuckDB DATE format (days) to milliseconds since epoch
 */
export function duckDBDateToMs(days: DuckDBDateValue): number {
  return days * DUCKDB_TEMPORAL_CONSTANTS.SECONDS_PER_DAY * 1000;
}
